import { Button } from "@/components/ui/button";
import { Sparkles, FlaskConical, CalendarClock, ArrowLeftRight, ShieldAlert, MessageCircleQuestion, Gauge, AlertTriangle, ClipboardList } from "lucide-react";
import { formatDate } from "@/lib/date";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";

// "AI Insights" tab — Overall Health Score, Risk Level, digest, missing
// investigations, changes since last visit, pending follow-up, clinical
// alerts and suggested discussion topics. Data/state owned by the parent.
export default function PatientAiInsightsTab({ aiSummary, aiLoading, generateSummary }) {
  const isReadOnly = useIsGracePeriod();
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <Sparkles className="h-4 w-4 text-teal-700" /> AI Insights
          </h2>
          {aiSummary && (
            <p className="mt-1 text-xs text-slate-500">
              Last generated {formatDate(aiSummary.generated_at)} by {aiSummary.generated_by}
            </p>
          )}
        </div>
        <Button
          data-testid="generate-ai-summary-btn"
          disabled={aiLoading || isReadOnly}
          title={isReadOnly ? "Disabled during subscription grace period" : undefined}
          onClick={generateSummary}
          className="gap-2 bg-teal-700 hover:bg-teal-800"
        >
          <Sparkles className="h-4 w-4" /> {aiLoading ? "Generating..." : aiSummary ? "Regenerate Insights" : "Generate Insights"}
        </Button>
      </div>

      {!aiSummary && !aiLoading && (
        <p className="mt-6 text-sm text-slate-400" data-testid="ai-summary-empty-state">
          No AI summary generated yet. Click "Generate Insights" to create one from this patient's recorded data.
        </p>
      )}

      {aiSummary && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="ai-summary-results">
          <InsightCard testId="ai-health-score-card" title="Overall Health Score" icon={Gauge}>
            <p className="text-3xl font-bold text-teal-700" data-testid="ai-health-score">
              {aiSummary.overall_health_score}
              <span className="text-sm text-slate-400">/100</span>
            </p>
            {aiSummary.overall_health_score_reasons?.length > 0 && (
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-600" data-testid="ai-health-score-reasons">
                {aiSummary.overall_health_score_reasons.map((r, i) => <li key={`${i}-${r}`}>{r}</li>)}
              </ul>
            )}
          </InsightCard>

          <InsightCard testId="ai-risk-level-card" title="Risk Level" icon={AlertTriangle}>
            <span
              data-testid="ai-risk-level"
              className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${
                { LOW: "bg-green-100 text-green-700", MODERATE: "bg-amber-100 text-amber-700", HIGH: "bg-red-100 text-red-700" }[aiSummary.risk_level] || "bg-slate-100 text-slate-700"
              }`}
            >
              {aiSummary.risk_level}
            </span>
            {aiSummary.risk_level_reasons?.length > 0 && (
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-600" data-testid="ai-risk-level-reasons">
                {aiSummary.risk_level_reasons.map((r, i) => <li key={`${i}-${r}`}>{r}</li>)}
              </ul>
            )}
          </InsightCard>

          <InsightBlock testId="ai-management-plan" title="AI Management Plan" icon={ClipboardList} items={aiSummary.ai_management_plan} emptyText="No specific next-step suggestions flagged." />

          <InsightBlock testId="ai-missing-investigations" title="Missing Investigations" icon={FlaskConical} items={aiSummary.missing_investigations} emptyText="None flagged — all recommended investigations appear up to date." />

          <InsightBlock testId="ai-changes-since-last-visit" title="Changes Since Last Visit" icon={ArrowLeftRight} items={aiSummary.changes_since_last_visit} emptyText="Not enough visit history for a comparison yet." />

          <InsightCard testId="ai-pending-followup" title="Pending Follow-up" icon={CalendarClock}>
            <p className="text-sm text-slate-600">
              {aiSummary.pending_followup?.is_pending ? aiSummary.pending_followup.details : "No pending follow-up."}
            </p>
          </InsightCard>

          <InsightBlock testId="ai-clinical-alerts" title="Clinical Alerts" icon={ShieldAlert} items={aiSummary.clinical_alerts} emptyText="No clinical alerts." alertStyle />
          <InsightBlock testId="ai-discussion-topics" title="Suggested Discussion Topics" icon={MessageCircleQuestion} items={aiSummary.suggested_discussion_topics} emptyText="No specific topics flagged for the next consultation." />
        </div>
      )}

      {aiSummary && <p className="mt-4 border-t border-slate-100 pt-3 text-xs italic text-slate-400">{aiSummary.data_limitations_note}</p>}

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        This summary is generated strictly from this patient's recorded prescriptions, vitals and history — it never diagnoses, recommends treatment, or fabricates values. Always use clinical judgment.
      </div>
    </>
  );
}

function InsightCard({ title, icon: Icon, children, testId }) {
  return (
    <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid={testId}>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
        <Icon className="h-4 w-4 text-teal-700" /> {title}
      </p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function InsightBlock({ title, icon: Icon, items, emptyText, alertStyle, testId }) {
  const list = Array.isArray(items) ? items : items ? [items] : [];
  const hasItems = list.length > 0;
  return (
    <div
      className={`card-surface card-surface-hover rounded-xl border shadow-sm p-6 ${alertStyle && hasItems ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}
      data-testid={testId}
    >
      <p className={`flex items-center gap-2 text-sm font-semibold ${alertStyle && hasItems ? "text-red-700" : "text-slate-900"}`}>
        <Icon className="h-4 w-4" /> {title}
      </p>
      {hasItems ? (
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
          {list.map((item, i) => (
            <li key={`${i}-${item}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-sm text-slate-400">{emptyText}</p>
      )}
    </div>
  );
}
